import { BillLineItem, BillSortValue } from "@common/types/bill";
import { BillLineItemColumn } from "@common/types/views/billViews";
import { Ledger } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ViewConfig } from "@lib/views";
import {
    buildLedgerAccountColumnIdByName,
    buildLedgerPayableColumnIdByName,
    uniqueLedgersByBasisAndName,
} from "@lib/ledgerViewColumns";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
    SCHEDULE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { DateCell } from "./cells/DateCell";
import { LockCell } from "./cells/LockCell";
import { StatusCell } from "./cells/StatusCell";
import { VendorCell } from "./cells/VendorCell";
import { AccountCell } from "./cells/AccountCell";
import { AccrualLedgerCell } from "./cells/AccrualLedgerCell";
import { AccountsPayableCell } from "./cells/AccountsPayableCell";
import { AmountCell } from "./cells/AmountCell";
import { OpenAmountCell } from "./cells/OpenAmountCell";
import { EntityCell } from "./cells/EntityCell";
import { ExternalLinkCell } from "./cells/ExternalLinkCell";
import { DescriptionCell } from "./cells/DescriptionCell";
import { ClassesCell } from "./cells/ClassesCell";
import { BillNumberCell } from "./cells/BillNumberCell";
import { ScheduleCell } from "./cells/ScheduleCell";
import { MatchCell } from "./cells/MatchCell";

interface CreateBillLineItemsViewConfigParams {
    hasAnyAccrualLedgers: boolean;
    isClassesEnabled: boolean;
    isSchedulesEnabled?: boolean;
    multiLedgerLedgers?: Ledger[];
}

export const createBillLineItemsViewConfig = ({
    hasAnyAccrualLedgers,
    isClassesEnabled,
    isSchedulesEnabled = false,
    multiLedgerLedgers = [],
}: CreateBillLineItemsViewConfigParams): ViewConfig<
    BillLineItem,
    string,
    BillSortValue
> => {
    const accrualLedgers = uniqueLedgersByBasisAndName(
        multiLedgerLedgers.filter(
            (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
        ),
    );
    const isMultiLedgerEnabled = accrualLedgers.length > 0;

    return {
        columns: {
            [BillLineItemColumn.LOCK]: {
                accessorKey: "id",
                name: "Lock",
                header: "",
                cell: LockCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 32,
                maxSize: 32,
            },
            [BillLineItemColumn.DATE]: {
                accessorKey: "bill.dateOnly",
                name: "Created date",
                header: "Created date",
                cell: DateCell,
                sort: {
                    asc: BillSortValue.DATE_ASC,
                    desc: BillSortValue.DATE_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [BillLineItemColumn.DESCRIPTION]: {
                accessorKey: "description",
                name: "Description",
                header: "Description",
                cell: DescriptionCell,
                minSize: 300,
                maxSize: 300,
            },
            ...(isClassesEnabled
                ? {
                      [BillLineItemColumn.CLASS]: {
                          accessorKey: "classAssignments",
                          name: "Class",
                          header: "Class",
                          cell: ClassesCell,
                          minSize: 220,
                          maxSize: 220,
                      },
                  }
                : {}),
            [BillLineItemColumn.STATUS]: {
                accessorKey: "bill.status",
                name: "Status",
                header: "",
                cell: StatusCell,
                sort: {
                    asc: BillSortValue.STATUS_ASC,
                    desc: BillSortValue.STATUS_DESC,
                },
                minSize: 24,
                maxSize: 24,
                internal: !hasAnyAccrualLedgers,
            },
            [BillLineItemColumn.EXTERNAL_STATUS]: {
                accessorKey: "bill.externalStatus",
                name: "External status",
                header: "",
                cell: StatusCell,
                sort: {
                    asc: BillSortValue.EXTERNAL_STATUS_ASC,
                    desc: BillSortValue.EXTERNAL_STATUS_DESC,
                },
                minSize: 24,
                maxSize: 24,
            },
            [BillLineItemColumn.VENDOR]: {
                accessorKey: "bill.counterparty",
                name: "Vendor",
                header: "Vendor",
                cell: VendorCell,
                sort: {
                    asc: BillSortValue.COUNTERPARTY_ASC,
                    desc: BillSortValue.COUNTERPARTY_DESC,
                },
                minSize: 200,
                maxSize: 400,
            },
            [BillLineItemColumn.ACCOUNT]: {
                accessorKey: "bill.financialAccount",
                name: "Account",
                header: "Account",
                cell: AccountCell,
                sort: {
                    asc: BillSortValue.ACCOUNT_ASC,
                    desc: BillSortValue.ACCOUNT_DESC,
                },
                minSize: 120,
                maxSize: 180,
            },
            [BillLineItemColumn.ACCRUAL_ACCOUNT]: {
                accessorKey: "accountId",
                name: "Expense account",
                header: "Expense account",
                cell: AccrualLedgerCell,
                minSize: 120,
                maxSize: 260,
                internal: isMultiLedgerEnabled,
            },
            [BillLineItemColumn.ACCOUNTS_PAYABLE]: {
                accessorKey: "accountsPayableId",
                name: "Payables account",
                header: "Payables account",
                cell: AccountsPayableCell,
                minSize: 120,
                maxSize: 260,
                internal: !hasAnyAccrualLedgers || isMultiLedgerEnabled,
            },
            ...Object.fromEntries(
                accrualLedgers.flatMap((ledger) => [
                    [
                        buildLedgerAccountColumnIdByName(ledger),
                        {
                            accessorKey:
                                buildLedgerAccountColumnIdByName(ledger),
                            name: ledger.name,
                            header: ledger.name,
                            cell: AccrualLedgerCell,
                            minSize: 120,
                            maxSize: 260,
                        },
                    ],
                    [
                        buildLedgerPayableColumnIdByName(ledger),
                        {
                            accessorKey:
                                buildLedgerPayableColumnIdByName(ledger),
                            name: `${ledger.name} payables account`,
                            header: `${ledger.name} payables account`,
                            cell: AccountsPayableCell,
                            minSize: 120,
                            maxSize: 260,
                        },
                    ],
                ]),
            ),
            [BillLineItemColumn.DUE_DATE]: {
                accessorKey: "bill.dueDateOnly",
                name: "Due date",
                header: "Due date",
                cell: DateCell,
                sort: {
                    asc: BillSortValue.DUE_DATE_ASC,
                    desc: BillSortValue.DUE_DATE_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [BillLineItemColumn.AMOUNT]: {
                accessorKey: "amount",
                name: "Amount",
                header: "Amount",
                cell: AmountCell,
                sort: {
                    asc: BillSortValue.AMOUNT_ASC,
                    desc: BillSortValue.AMOUNT_DESC,
                },
                ...AMOUNT_COLUMN_SIZE,
            },
            [BillLineItemColumn.OPEN]: {
                accessorKey: "amount",
                name: "Open",
                header: "Open",
                cell: OpenAmountCell,
                ...AMOUNT_COLUMN_SIZE,
            },
            [BillLineItemColumn.INVOICE_NUMBER]: {
                accessorKey: "bill.invoiceNumber",
                name: "Bill number",
                header: "Bill number",
                cell: BillNumberCell,
                minSize: 120,
                maxSize: 120,
            },
            [BillLineItemColumn.SCHEDULE]: {
                accessorKey: "schedule.scheduleName",
                name: "Expense schedule",
                header: "Expense schedule",
                cell: ScheduleCell,
                ...SCHEDULE_COLUMN_SIZE,
                internal: !isSchedulesEnabled,
            },
            [BillLineItemColumn.MATCH]: {
                accessorKey: "billId",
                name: "Match",
                header: "Match",
                cell: MatchCell,
                minSize: 140,
                maxSize: 220,
                internal: !hasAnyAccrualLedgers,
            },
            [BillLineItemColumn.ENTITY]: {
                accessorKey: "bill.financialAccount.entity",
                name: "Entity",
                header: "",
                cell: EntityCell,
                minSize: 42,
                maxSize: 42,
            },
            [BillLineItemColumn.EXTERNAL_LINK]: {
                accessorKey: "bill.id",
                name: "External Link",
                header: "",
                cell: ExternalLinkCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 48,
                maxSize: 48,
            },
        },
    };
};
